import React, { useEffect, useState, useRef } from "react";
import { Mail, Phone, MapPin, Send, ArrowRight, Sparkles } from "lucide-react";
import { FaFacebookF, FaInstagram, FaWhatsapp } from "react-icons/fa";

const Contact = () => {
  const [visible, setVisible] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const top = sectionRef.current.getBoundingClientRect().top;
      if (top < window.innerHeight * 0.7) setVisible(true);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const contactCards = [
    { 
      icon: Mail, 
      title: "البريد الإلكتروني",
      text: "info@example.com",
      gradient: "from-blue-500 to-cyan-500",
      link: "mailto:info@example.com"
    },
    { 
      icon: Phone, 
      title: "رقم الهاتف",
      text: "+20 123 456 789",
      gradient: "from-purple-500 to-pink-500",
      link: "tel:+20123456789"
    },
    { 
      icon: MapPin, 
      title: "الموقع",
      text: "أسوان - مركز إدفو",
      gradient: "from-orange-500 to-red-500",
      link: "#"
    },
  ];

  const socialLinks = [
    { 
      icon: FaFacebookF, 
      href: "https://www.facebook.com/kemodaana22", 
      color: "from-blue-600 to-blue-700",
      hoverColor: "group-hover:shadow-blue-500/50",
      label: "Facebook"
    },
    { 
      icon: FaInstagram, 
      href: "https://www.instagram.com/Kemaa111", 
      color: "from-pink-500 to-purple-600",
      hoverColor: "group-hover:shadow-pink-500/50",
      label: "Instagram"
    },
    { 
      icon: FaWhatsapp, 
      href: "https://wa.me/20123456789", 
      color: "from-green-500 to-emerald-600",
      hoverColor: "group-hover:shadow-green-500/50",
      label: "WhatsApp"
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative bg-gradient-to-b from-white via-gray-50 to-gray-100 py-32 overflow-hidden"
      id="contact"
    >
      {/* Background Decoration */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 right-20 w-96 h-96 bg-blue-200 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-purple-200 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <div
            className={`inline-flex items-center gap-3 bg-gray-900 text-white px-6 py-3 rounded-full mb-8 transition-all duration-1000 shadow-lg ${
              visible ? "opacity-100 scale-100" : "opacity-0 scale-75"
            }`}
          >
            <Sparkles className="w-5 h-5 text-gray-400 animate-pulse" />
            <span className="font-medium tracking-wider">GET IN TOUCH</span>
          </div>
          
          <h2
            className={`text-5xl md:text-6xl font-black text-gray-900 mb-6 transition-all duration-1000 delay-100 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            تواصل معنا
          </h2>
          
          <p
            className={`text-gray-600 text-xl max-w-2xl mx-auto transition-all duration-1000 delay-200 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            نحن هنا لتحويل أفكارك إلى واقع مذهل
          </p>

          <div
            className={`w-24 h-1.5 bg-gradient-to-r from-gray-900 to-gray-600 mx-auto mt-6 rounded-full transition-all duration-1000 delay-300 ${
              visible ? "opacity-100 scale-x-100" : "opacity-0 scale-x-0"
            }`}
          />
        </div>

        {/* Contact Cards */}
        <div
          className={`grid md:grid-cols-3 gap-8 mb-20 transition-all duration-1000 delay-400 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"
          }`}
        >
          {contactCards.map((card, idx) => {
            const Icon = card.icon;
            const isHovered = hoveredCard === idx;
            
            return (
              <a
                key={idx}
                href={card.link}
                className="group relative block"
                onMouseEnter={() => setHoveredCard(idx)}
                onMouseLeave={() => setHoveredCard(null)}
              >
                {/* Glow Effect */}
                <div
                  className={`absolute -inset-1 bg-gradient-to-r ${card.gradient} rounded-3xl blur-xl opacity-0 group-hover:opacity-30 transition-all duration-700`}
                />

                {/* Card */}
                <div className="relative bg-white rounded-3xl p-8 shadow-lg group-hover:shadow-2xl transition-all duration-500 border border-gray-200 group-hover:border-transparent overflow-hidden h-full">
                  {/* Gradient Background on Hover */}
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}
                  />

                  {/* Content */}
                  <div className="relative z-10 text-center">
                    {/* Icon */}
                    <div className="mb-6 relative inline-block">
                      <div
                        className={`absolute inset-0 bg-gradient-to-r ${card.gradient} blur-2xl opacity-0 group-hover:opacity-50 transition-opacity duration-700`}
                      />
                      <div className={`relative w-20 h-20 bg-gradient-to-br ${card.gradient} rounded-2xl flex items-center justify-center mx-auto transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-700 shadow-xl`}>
                        <Icon className="w-10 h-10 text-white" />
                      </div>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {card.title}
                    </h3>

                    {/* Text */}
                    <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300 mb-4">
                      {card.text}
                    </p>

                    {/* Arrow */}
                    <div
                      className={`flex items-center justify-center gap-2 text-sm font-medium bg-gradient-to-r ${card.gradient} bg-clip-text text-transparent transform transition-all duration-500 ${
                        isHovered ? "translate-x-2 opacity-100" : "opacity-0"
                      }`}
                    >
                      <span>تواصل الآن</span>
                      <ArrowRight className="w-4 h-4" style={{ color: 'currentColor' }} />
                    </div>
                  </div>

                  {/* Bottom Border */}
                  <div className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r ${card.gradient} w-0 group-hover:w-full transition-all duration-700 rounded-full`} />
                </div>
              </a>
            );
          })}
        </div>

        {/* Social Links Section */}
        <div
          className={`max-w-4xl mx-auto transition-all duration-1000 delay-600 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-20"
          }`}
        >
          <div className="bg-gray-900 rounded-3xl p-10 shadow-2xl">
            <div className="text-center mb-10">
              <h3 className="text-4xl font-bold text-white mb-4">
                تابعنا على
              </h3>
              <p className="text-gray-400 text-lg">
                ابقَ على اطلاع بأحدث أعمالنا وعروضنا الخاصة
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-10">
              {socialLinks.map((link, idx) => {
                const Icon = link.icon;
                
                return (
                  <a
                    key={idx}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`group flex flex-col items-center gap-4 bg-white/5 hover:bg-white/10 backdrop-blur-sm border border-white/10 rounded-2xl p-6 transition-all duration-500 hover:scale-105 hover:border-white/20 ${link.hoverColor}`}
                  >
                    <div className={`w-16 h-16 bg-gradient-to-br ${link.color} rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-2xl`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    
                    <div className="text-center">
                      <p className="text-white font-bold text-lg mb-1">{link.label}</p>
                      <p className="text-gray-400 text-sm">تابعنا الآن</p>
                    </div>

                    <ArrowRight className="w-5 h-5 text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all duration-300" />
                  </a>
                );
              })}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-white/10">
              <div className="text-center">
                <p className="text-4xl font-black text-white mb-2">500+</p>
                <p className="text-gray-400">مشروع مكتمل</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-black text-white mb-2">300+</p>
                <p className="text-gray-400">عميل سعيد</p>
              </div>
              <div className="text-center">
                <p className="text-4xl font-black text-white mb-2">5+</p>
                <p className="text-gray-400">سنوات خبرة</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;